import '../../platform.css';
import './settings.css';

import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { tenantSettingsState } from '../../recoil/atoms/tenantSettingsState';
import { navigationPropsState } from '../../recoil/atoms/navigationPropsState';
import { formationsInUseState } from '../../recoil/atoms/formationsInUseState';
import { useSetNavigationProps } from '../../recoil/hooks/useSetNavigationProps';
import { useAuthContext } from '../../../common/contexts/useAuthContext';
import { useOpenGlobalModal } from '../../recoil/hooks/useOpenGlobalModal';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CheckIcon from '@mui/icons-material/Check';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EmergencyIcon from '@mui/icons-material/Emergency';
import ReplayIcon from '@mui/icons-material/Replay';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SwipeDownIcon from '@mui/icons-material/SwipeDown';
import UpdateIcon from '@mui/icons-material/Update';

import { DefaultPlatformFormation, FormationConfig, PlatformFormation } from '../../types/formation';
import { formationConfigsAreEqual, getFormationOptions } from '../../utils/formationUtils';
import { defaultFormationConfigMap, defaultFormationToSortOrder, maxFormationSortOrder } from '../../static/constants/formations';
import { IconButton } from '../../components/controls/buttons/IconButton';
import { translate } from '../../../common/language/translations';
import { InputField } from '../../components/controls/input/InputField';
import { FootballFieldSvg } from '../../../assets/svg/FootballFieldSvg';
import { getGridPosition } from '../../utils/positionUtils';
import { groupedPositionOptionsPlatfrom } from '../../static/constants/positions';
import { PlatformPosition } from '../../types/position';
import { getTenantColor } from '../../static/tenantConfigs';
import { TextButton } from '../../components/controls/buttons/TextButton';
import { updateFormationConfig, updateTenantFormation } from '../../services/firestore/tenantSettings';
import { DropDown } from '../../components/controls/dropDowns/DropDown';
import React from 'react';
import { updateTeamsWithNewFormation } from '../../services/firestore/teams';
import { trackEvent } from '../../services/server/analytics/trackEvent';


interface SelectedFormationConfig {
  id: string | undefined; // undefined for new formations, otherwise the name of the formation
  name: string; // we need to separate the name from the id to allow to edit the name
  initialConfig: FormationConfig;
  currentConfig: FormationConfig;
  isEditMode: boolean;
  isDefaultFormation: boolean;
  isEditedFormation: boolean;
}


export const Formations: React.FC = () => {

  const { currentUser } = useAuthContext();
  const { openConfirmModal, openTextModal } = useOpenGlobalModal();

  const userConfig = useRecoilValue(userConfigState);
  const tenantSettings = useRecoilValue(tenantSettingsState);
  const formationsInUse = useRecoilValue(formationsInUseState);

  const navigationProps = useRecoilValue(navigationPropsState);
  const updateNavigationProps = useSetNavigationProps();

  const formationsContainerRef = useRef<HTMLDivElement>(null);


  const formationOptions = getFormationOptions(tenantSettings?.formationConfigs);
  const [selectedTenantFormation, setSelectedTenantFormation] = useState<PlatformFormation[] | undefined>(
    tenantSettings?.formation ? [tenantSettings.formation] : undefined
  );
  const [isFormationDropDownExpanded, setIsFormationDropDownExpanded] = useState(false);


  const [selectedFormationConfig, setSelectedFormationConfig] = useState<SelectedFormationConfig | undefined>(undefined);

  const [showAddOrEditContent, setShowAddOrEditContent] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);


  const [draggedPosition, setDraggedPosition] = useState<{ position: PlatformPosition } | undefined>(undefined);
  const [gridCellDraggedOver, setGridCellDraggedOver] = useState<string | undefined>(undefined);


  const handleUpdateTenantFormation = async () => {
    if (userConfig && tenantSettings && selectedTenantFormation && selectedTenantFormation.length > 0) {
      updateTenantFormation(
        selectedTenantFormation[0],
        tenantSettings,
        userConfig.email,
        userConfig.club,
        currentUser,
      );
    }
  };


  const setFormationConfigName = (name: string) => {
    if (selectedFormationConfig) {
      if (name.length > 14) {
        setErrorMessage('max14CharactersInFormationName');
        return;
      }

      setSelectedFormationConfig({ ...selectedFormationConfig, name });
      updateNavigationProps({ formationConfigHasUnsavedChanges: true });
      setErrorMessage(undefined);
    }
  };


  const handleDragStart = (position: PlatformPosition, isNewPosition?: boolean) => {
    if (isNewPosition && selectedFormationConfig && Object.keys(selectedFormationConfig.currentConfig).length >= 16) {
      setErrorMessage('max16PositionsInFormation');
      return false;
    }

    setDraggedPosition({ position });
    return true;
  };


  const handleDragEnd = () => {
    setDraggedPosition(undefined);
    setGridCellDraggedOver(undefined);
    setErrorMessage(undefined);
  };


  const handleDrop = (event: React.DragEvent<HTMLDivElement>, x: number, y: number) => {
    event.preventDefault();

    if (draggedPosition && selectedFormationConfig) {
      setSelectedFormationConfig({
        ...selectedFormationConfig,
        currentConfig: {
          ...selectedFormationConfig.currentConfig,
          [draggedPosition.position]: { x, y },
        },
      });

      trackEvent('FormationConfigPositionMoved', {}, currentUser, 'user');
    }

    handleDragEnd();
    updateNavigationProps({ formationConfigHasUnsavedChanges: true });
  };


  const handleRemovePositionDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (draggedPosition && selectedFormationConfig) {
      const newConfig = { ...selectedFormationConfig.currentConfig };
      delete newConfig[draggedPosition.position];
      setSelectedFormationConfig({ ...selectedFormationConfig, currentConfig: newConfig });

      trackEvent('FormationConfigPositionMoved', {}, currentUser, 'user');
    }

    handleDragEnd();
    updateNavigationProps({ formationConfigHasUnsavedChanges: true });
  };


  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };


  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>, gridId: string) => {
    event.preventDefault();
    setGridCellDraggedOver(gridId);
  };


  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setGridCellDraggedOver(undefined);
  };


  const openConfirmExitFormationModal = (confirm: () => void) => openConfirmModal(confirm, 'exitFormation?', 'exitFormationInfo');


  const setNewFormationConfig = (formation: PlatformFormation) => {
    if (selectedFormationConfig?.id === formation) {
      setSelectedFormationConfig(undefined);
      return;
    }

    setSelectedFormationConfig({
      id: formation,
      name: formation,
      initialConfig: tenantSettings?.formationConfigs?.[formation] ?? defaultFormationConfigMap[formation],
      currentConfig: tenantSettings?.formationConfigs?.[formation] ?? defaultFormationConfigMap[formation],
      isEditMode: false,
      isDefaultFormation: defaultFormationConfigMap[formation] !== undefined,
      isEditedFormation: tenantSettings?.formationConfigs?.[formation] !== undefined,
    });

    trackEvent('FormationConfigViewed', {}, currentUser, 'user');

    updateNavigationProps({ formationConfigHasUnsavedChanges: false });
    setErrorMessage(undefined);
  };


  const onFormationOptionClick = (formation: PlatformFormation) => {
    if (navigationProps.formationConfigHasUnsavedChanges) {
      openConfirmExitFormationModal(() => setNewFormationConfig(formation));
    }
    else {
      setNewFormationConfig(formation);
    }
  };


  const exitFormation = () => {
    setSelectedFormationConfig(undefined);
    updateNavigationProps({ formationConfigHasUnsavedChanges: false });
    setErrorMessage(undefined);
  };


  const onCloseIconClick = () => {
    if (navigationProps.formationConfigHasUnsavedChanges) {
      openConfirmExitFormationModal(exitFormation);
    }
    else {
      exitFormation();
    }
  };


  const setAddNewFormationConfig = () => {
    if (selectedFormationConfig && selectedFormationConfig.id === undefined) {
      setSelectedFormationConfig(undefined);
      return;
    }

    setSelectedFormationConfig({
      id: undefined,
      name: '',
      initialConfig: {},
      currentConfig: {},
      isEditMode: false,
      isDefaultFormation: false,
      isEditedFormation: false,
    });

    updateNavigationProps({ formationConfigHasUnsavedChanges: false });
    setErrorMessage(undefined);
  };


  const onAddNewFormationClick = () => {
    if (navigationProps.formationConfigHasUnsavedChanges) {
      openConfirmExitFormationModal(setAddNewFormationConfig);
    }
    else {
      setAddNewFormationConfig();
    }
  };


  const handleAddOrEditFormation = async (formationIsInUse: boolean) => {
    if (selectedFormationConfig && userConfig) {

      // if this is a default formation and we are editing it back to the default config, we essentially delete the formation
      const formationIsEditedBackToDefault = selectedFormationConfig.isDefaultFormation &&
        selectedFormationConfig.id !== undefined &&
        selectedFormationConfig.id === selectedFormationConfig.name &&
        formationConfigsAreEqual(selectedFormationConfig.currentConfig, defaultFormationConfigMap[selectedFormationConfig.id]);

      if (formationIsEditedBackToDefault && selectedFormationConfig.id) {
        await resetToDefaultFormation(formationIsInUse);
      }

      else {
        setSelectedFormationConfig({
          ...selectedFormationConfig,
          id: selectedFormationConfig.name,
          initialConfig: selectedFormationConfig.currentConfig,
          currentConfig: selectedFormationConfig.currentConfig,
          isEditMode: false,
          isEditedFormation: !formationIsEditedBackToDefault,
          isDefaultFormation: defaultFormationConfigMap[selectedFormationConfig.name] !== undefined,
        });

        // if formation is used, we need to update all teams with this formation
        // either the name is edited (we treat this as a new formation) or the config is edited (in which case we update the teams to align with the new config)
        if (selectedFormationConfig.id && formationIsInUse) {
          updateTeamsWithNewFormation(
            selectedFormationConfig.name,
            selectedFormationConfig.currentConfig,
            selectedFormationConfig.id,
            selectedFormationConfig.initialConfig,
            userConfig.email,
            userConfig.club,
            currentUser,
          );
        }

        await updateFormationConfig(
          selectedFormationConfig.id,
          selectedFormationConfig.name,
          selectedFormationConfig.currentConfig,
          userConfig.club,
          currentUser,
        );
      }

      updateNavigationProps({ formationConfigHasUnsavedChanges: false });
      setErrorMessage(undefined);
    }
  };


  const validateFormationConfig = () => {
    if (selectedFormationConfig) {
      const noChangesMade = selectedFormationConfig.id !== undefined &&
        selectedFormationConfig.id === selectedFormationConfig.name &&
        formationConfigsAreEqual(selectedFormationConfig.initialConfig, selectedFormationConfig.currentConfig);

      if (noChangesMade) {
        setSelectedFormationConfig({ ...selectedFormationConfig, isEditMode: false });
        return false;
      }

      // name must be provided and unique
      if (!selectedFormationConfig.name) {
        setErrorMessage('youMustProvideNameOfFormation');
        return false;
      }

      const nameHasBeenEdited = selectedFormationConfig.id === undefined || selectedFormationConfig.id !== selectedFormationConfig.name;
      if (nameHasBeenEdited && formationOptions.includes(selectedFormationConfig.name)) {
        setErrorMessage('formationNameMustBeUnique');
        return false;
      }

      // at least one position must be provided
      if (Object.keys(selectedFormationConfig.currentConfig).length === 0) {
        setErrorMessage('youMustProvideAtLeastOnePosition');
        return false;
      }

      return true;
    }
  };


  const onConfirmIconClick = async () => {
    if (!validateFormationConfig() || !selectedFormationConfig) return;

    const anyPositionsHaveBeenRemoved = selectedFormationConfig.id !== undefined &&
      Object.keys(selectedFormationConfig.initialConfig).some(position => !selectedFormationConfig.currentConfig[position as PlatformPosition]);

    const formationIsInUse = selectedFormationConfig.id !== undefined &&
      formationsInUse.includes(selectedFormationConfig.id as PlatformFormation);

    // if formation is in use and any positions have been removed, the update may cause players to be moved to new positions
    const shoulOpenConfirmModal = anyPositionsHaveBeenRemoved && formationIsInUse;
    if (shoulOpenConfirmModal) {
      openConfirmModal(
        () => handleAddOrEditFormation(formationIsInUse),
        'editFormation',
        'confirmEditFormationInfo',
      );
    }
    else {
      handleAddOrEditFormation(formationIsInUse);
    }
  };


  const deleteFormation = async (formationIsInUse: boolean) => {
    if (selectedFormationConfig?.id && userConfig && tenantSettings) {
      setSelectedFormationConfig(undefined);

      if (formationIsInUse) {
        updateTeamsWithNewFormation(
          tenantSettings.formation,
          tenantSettings.formationConfigs?.[tenantSettings.formation] ?? defaultFormationConfigMap[tenantSettings.formation],
          selectedFormationConfig.id,
          selectedFormationConfig.initialConfig,
          userConfig.email,
          userConfig.club,
          currentUser,
        );
      }

      await updateFormationConfig(
        selectedFormationConfig.id,
        undefined,
        undefined,
        userConfig.club,
        currentUser,
      );

      updateNavigationProps({ formationConfigHasUnsavedChanges: false });
      setErrorMessage(undefined);
    }
  };


  const onDeleteFormationClick = async () => {
    if (!selectedFormationConfig?.id) return;

    // we do not allow to delete the tenant formation
    if (selectedFormationConfig.id === tenantSettings?.formation) {
      setErrorMessage('youCannotDeleteClubFormation');
      return;
    }

    const formationIsInUse = formationsInUse.includes(selectedFormationConfig.id as PlatformFormation);

    openConfirmModal(
      () => deleteFormation(true),
      'deleteFormation',
      formationIsInUse ? 'deleteFormationInfo' : 'noRegretInfo',
    );
  };


  const resetToDefaultFormation = async (formationIsInUse: boolean) => {
    if (selectedFormationConfig?.id && userConfig) {

      if (formationIsInUse) {
        updateTeamsWithNewFormation(
          selectedFormationConfig.id,
          defaultFormationConfigMap[selectedFormationConfig.id],
          selectedFormationConfig.id,
          selectedFormationConfig.initialConfig,
          userConfig.email,
          userConfig.club,
          currentUser,
        );
      }

      setSelectedFormationConfig({
        ...selectedFormationConfig,
        id: selectedFormationConfig.name,
        initialConfig: defaultFormationConfigMap[selectedFormationConfig.id],
        currentConfig: defaultFormationConfigMap[selectedFormationConfig.id],
        isEditMode: false,
        isEditedFormation: false,
      });

      await updateFormationConfig(
        selectedFormationConfig.id,
        undefined,
        undefined,
        userConfig.club,
        currentUser,
      );

      updateNavigationProps({ formationConfigHasUnsavedChanges: false });
      setErrorMessage(undefined);
    }
  };


  const onResetToDefaultFormationClick = async () => {

    if (!selectedFormationConfig?.id) return;

    const configInStore = selectedFormationConfig.initialConfig;
    const newConfig = defaultFormationConfigMap[selectedFormationConfig.id];

    if (!newConfig) return;

    // if formation is in use and any positions have been removed, the update may cause players to be moved to new positions
    const anyPositionsWillBeRemoved = Object.keys(configInStore).some(position => !newConfig[position as PlatformPosition]);
    const formationIsInUse = formationsInUse.includes(selectedFormationConfig.id as PlatformFormation);

    openConfirmModal(
      () => resetToDefaultFormation(formationIsInUse),
      'resetFormation',
      anyPositionsWillBeRemoved && formationIsInUse ? 'resetFormationInfo' : 'noRegretInfo',
    );
  };


  const xValues = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
  const yValues = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];


  useEffect(() => {
    if (selectedFormationConfig?.isEditMode || (selectedFormationConfig && selectedFormationConfig.id === undefined)) {
      const timer = setTimeout(() => {
        setShowAddOrEditContent(true);
      }, 250);
      return () => clearTimeout(timer);
    }
    else {
      setShowAddOrEditContent(false);
    }
  }, [selectedFormationConfig]);


  const tenantColor = getTenantColor(userConfig?.club ?? '');


  const gridHeight = (formationsContainerRef.current?.clientHeight ?? 0) * 0.04;
  const getRowWidthPercentage = (y: number) => 75 + (y * 0.8);
  const getGridWidth = (rowWidthPercentage?: number) => (formationsContainerRef.current?.clientWidth ?? 0)
    * ((rowWidthPercentage ?? getRowWidthPercentage((yValues.length - 1) / 2)) / 100)
    * 0.055;


  const showDeleteFormationIcon = selectedFormationConfig &&
    selectedFormationConfig.id !== undefined &&
    selectedFormationConfig.isEditMode &&
    !selectedFormationConfig.isDefaultFormation;


  const showResetToDefaultIcon = selectedFormationConfig &&
    selectedFormationConfig.id !== undefined &&
    selectedFormationConfig.isEditMode &&
    selectedFormationConfig.isDefaultFormation &&
    selectedFormationConfig.isEditedFormation;


  return (
    <div className='full-size-container formations-view-container'>

      {isFormationDropDownExpanded && <div className='filter-section-empty-background' onClick={() => setIsFormationDropDownExpanded(false)} />}

      <div className='formations-view-left-container'>
        <div
          className='formations-view-formation-options-container'
          style={{
            height: userConfig?.accessConfig.hasAccessToManageFormations ? 'calc(90% - 40px)' : 'calc(100% - 40px)',
            maxHeight: userConfig?.accessConfig.hasAccessToManageFormations ? 'calc(90% - 40px)' : 'calc(100% - 40px)',
          }}
        >
          <div className='formations-view-formation-options'>
            {formationOptions.map((formation, index) => {

              // we want to show a divider between the default formaiton and the custom tenant formations
              const shouldShowDivider = index > 0 && defaultFormationToSortOrder[formation as DefaultPlatformFormation] === maxFormationSortOrder;

              const isTenantFormation = tenantSettings?.formation === formation;
              const isEditedFormation = tenantSettings?.formationConfigs?.[formation] !== undefined;

              return (
                <React.Fragment key={formation}>

                  {shouldShowDivider && <div className='formations-view-horizontal-divider' />}

                  <div
                    className={
                      'settings-view-selectable-option formations-view-formation-option'
                      + (selectedFormationConfig?.id === formation ? ' settings-view-selectable-option-selected' : '')
                      + (selectedFormationConfig?.id === formation && selectedFormationConfig?.isEditMode ? ' settings-view-selectable-option-edited' : '')
                    }
                    onClick={() => onFormationOptionClick(formation)}
                  >
                    {isTenantFormation && (
                      <div className='formations-view-formation-option-icon-left'>
                        <CheckIcon style={{ fontSize: 15, marginBottom: -2 }} />
                      </div>
                    )}

                    {formation}

                    {isEditedFormation && (
                      <div className='formations-view-formation-option-icon-right'>
                        <EmergencyIcon style={{ fontSize: 12, marginBottom: -1 }} />
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {userConfig?.accessConfig.hasAccessToManageFormations && <div className='formations-view-horizontal-divider formations-view-horizontal-divider-bottom' />}

        {userConfig?.accessConfig.hasAccessToManageFormations && (
          <div className='formations-view-add-formation-option-container'>
            <TextButton
              onClick={onAddNewFormationClick}
              text={translate('newFormation', userConfig?.language)}
              icon={<AddIcon style={{ fontSize: 18, marginRight: -3 }} />}
              style={{
                width: 160,
                height: 26,
                fontSize: 13,
                color: selectedFormationConfig && selectedFormationConfig.id === undefined ? '#ffffff' : undefined,
                backgroundColor: selectedFormationConfig && selectedFormationConfig.id === undefined ? '#87a8ce3f' : undefined,
                boxShadow: selectedFormationConfig && selectedFormationConfig.id === undefined ? '0px 0px 0px 0.5px #c4d9ff' : undefined,
                fontWeight: selectedFormationConfig && selectedFormationConfig.id === undefined ? 500 : undefined,
              }}
            />
          </div>
        )}
      </div>

      <div className='formations-view-vertical-divider' />

      {selectedFormationConfig && (
        <div className='formations-view-right-container fade-in'>

          {errorMessage && (
            <div className='formations-view-error-message'>
              {translate(errorMessage, userConfig?.language)}
            </div>
          )}

          <div className='formations-view-name-container'>
            {showDeleteFormationIcon && (
              <div className='fade-in'>
                <IconButton
                  onClick={onDeleteFormationClick}
                  icon={<DeleteForeverIcon style={{ fontSize: 18, marginTop: -1 }} />}
                  size={26}
                  style={{ position: 'absolute', left: 13, zIndex: 5 }}
                  startWhite={true}
                  whiteBackgroundOnHover={true}
                  title={translate('deleteFormation', userConfig?.language)}
                />
              </div>
            )}

            {showResetToDefaultIcon && (
              <div className='fade-in'>
                <IconButton
                  onClick={onResetToDefaultFormationClick}
                  icon={<ReplayIcon style={{ fontSize: 24 }} />}
                  size={26}
                  style={{ position: 'absolute', left: 13, zIndex: 5 }}
                  startWhite={true}
                  whiteBackgroundOnHover={true}
                  title={translate('resetFormation', userConfig?.language)}
                />
              </div>
            )}

            {(showDeleteFormationIcon || showResetToDefaultIcon) && <div className='formations-view-name-container-description-divider fade-in' />}

            {(selectedFormationConfig.isEditMode || selectedFormationConfig.id === undefined) && (
              <div
                className='formations-view-name-container-description fade-in'
                style={{ left: 30 + (showDeleteFormationIcon || showResetToDefaultIcon ? 30 : 0) }}
              >
                {translate(selectedFormationConfig.isEditMode ? 'editFormation' : 'createNewFormation', userConfig?.language)}

                <IconButton
                  onClick={() => openTextModal(
                    translate(selectedFormationConfig.isEditMode ? 'editFormation' : 'createNewFormation', userConfig?.language),
                    translate('editOrCreateFormationInfo', userConfig?.language),
                    { infoType: 'infoIcon', titleKey: 'roleIndexInTeam' }
                  )}
                  icon={<InfoOutlinedIcon style={{ fontSize: 14 }} />}
                  size={20}
                  style={{ marginTop: 1, marginLeft: 6 }}
                />
              </div>
            )}

            {(selectedFormationConfig.id === undefined || selectedFormationConfig.isEditMode) && userConfig?.accessConfig.hasAccessToManageFormations && (
              <InputField
                value={selectedFormationConfig.name}
                placeholder={translate('provideNameofFormation', userConfig?.language)}
                onChange={(e) => setFormationConfigName(e.target.value)}
                resetValue={() => setFormationConfigName('')}
                width={200}
              />
            )}

            {selectedFormationConfig.id !== undefined && !selectedFormationConfig.isEditMode && (
              <div className='formations-view-name-container-title'>
                {selectedFormationConfig.name}
              </div>
            )}

            {(selectedFormationConfig.id === undefined || selectedFormationConfig.isEditMode) && userConfig?.accessConfig.hasAccessToManageFormations && (
              <IconButton
                onClick={onConfirmIconClick}
                icon={<CheckIcon style={{ fontSize: 24, marginTop: -1 }} />}
                size={26}
                style={{ position: 'absolute', right: 105, zIndex: 5 }}
                startWhite={true}
                whiteBackgroundOnHover={true}
                title={translate(selectedFormationConfig.isEditMode ? 'confirmEdits' : 'createFormation', userConfig?.language)}
              />
            )}

            {selectedFormationConfig.id !== undefined && !selectedFormationConfig.isEditMode && userConfig?.accessConfig.hasAccessToManageFormations && (
              <IconButton
                onClick={() => setSelectedFormationConfig({ ...selectedFormationConfig, isEditMode: true })}
                icon={<EditNoteIcon style={{ fontSize: 24, marginLeft: 2.5, marginTop: -1 }} />}
                size={26}
                style={{ position: 'absolute', right: 105, zIndex: 5 }}
                startWhite={true}
                whiteBackgroundOnHover={true}
                title={translate('editFormation', userConfig?.language)}
              />
            )}

            <IconButton
              onClick={onCloseIconClick}
              icon={<CloseIcon style={{ fontSize: 24 }} />}
              size={26}
              style={{ position: 'absolute', right: 50, zIndex: 5 }}
              startWhite={true}
              whiteBackgroundOnHover={true}
            />
          </div>

          <div
            className='formations-view-position-container'
            style={{
              height: selectedFormationConfig.id === undefined || selectedFormationConfig.isEditMode ? undefined : 0
            }}
          >
            {showAddOrEditContent && groupedPositionOptionsPlatfrom.map((positionColumn, index) => {

              return (
                <div
                  key={index}
                  className='formations-view-position-column fade-in'
                >
                  {positionColumn.map((positionRow, index) => {

                    return (
                      <div
                        key={index}
                        className='formations-view-position-row'
                      >
                        {positionRow.map(position => {

                          const positionIsInFormation = selectedFormationConfig.currentConfig[position];

                          const gridWidth = getGridWidth();

                          return (
                            <div
                              key={position}
                              className={
                                'formations-view-position-option'
                                + (positionIsInFormation ? ' formations-view-position-option-locked' : '')
                                + (!positionIsInFormation ? ' formations-view-position-option-draggable' : '')
                              }
                              draggable={!positionIsInFormation}
                              onDragStart={(e) => {
                                if (!handleDragStart(position, true)) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }
                              }}
                              onDragEnd={handleDragEnd}
                              style={{
                                width: gridWidth,
                                height: gridHeight,
                              }}
                            >
                              {translate(position, userConfig?.language)}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <div
            ref={formationsContainerRef}
            className='formations-view-team-container'
            style={{ height: selectedFormationConfig.id === undefined || selectedFormationConfig.isEditMode ? 'calc(100% - 155px)' : 'calc(100% - 75px)' }}
          >

            <FootballFieldSvg />

            {showAddOrEditContent && (
              <div className='formations-view-team-grid-container fast-fade-in'>

                {draggedPosition && (
                  <div
                    className='formations-view-delete-position-icon-container'
                    onDragOver={handleDragOver}
                    onDragEnter={(e) => handleDragEnter(e, 'deleteIcon')}
                    onDragLeave={(e) => handleDragLeave(e)}
                    onDrop={(e) => handleRemovePositionDrop(e)}
                    style={{ zIndex: 1 }}
                  />
                )}

                {draggedPosition && (
                  <div
                    className='formations-view-delete-position-icon-container fade-in'
                    style={{
                      boxShadow: gridCellDraggedOver === 'deleteIcon' ? '0px 0px 0px 3px #ff2424' : undefined,
                      backgroundColor: '#cdd0e758',
                    }}
                  >
                    {<DeleteForeverIcon style={{ fontSize: 24 }} />}
                  </div>
                )}

                <div className='formations-view-swipe-icon-container fade-in'>
                  <SwipeDownIcon style={{ fontSize: 20 }} />
                </div>

                {yValues.map(y => {

                  const rowWidthPercentage = getRowWidthPercentage(y);

                  return (
                    <div
                      key={y}
                      className='formations-view-team-grid-row'
                      style={{
                        height: gridHeight,
                        width: rowWidthPercentage + '%',
                      }}
                    >
                      {xValues.map(x => {

                        const gridWidth = getGridWidth(rowWidthPercentage);

                        const isGridOfExistingPosition = Object.values(selectedFormationConfig.currentConfig)
                          .some((coordinates) => coordinates.x === x && coordinates.y === y);

                        if (isGridOfExistingPosition) return (
                          <div key={x} style={{ width: gridWidth, height: gridHeight }} />
                        );

                        const isGridOfInitialPosition = Object.values(selectedFormationConfig.initialConfig)
                          .some((coordinates) => coordinates.x === x && coordinates.y === y);

                        const gridId = x + '_' + y;
                        const isDraggedOver = gridId === gridCellDraggedOver;

                        return (
                          <div
                            key={x}
                            className={
                              'formations-view-team-grid-cell'
                              + (draggedPosition ? ' formations-view-team-grid-cell-drag-active' : '')
                              + (isDraggedOver ? ' formations-view-team-grid-cell-dragged-over' : '')
                              + (isGridOfInitialPosition && !draggedPosition ? ' formations-view-team-grid-cell-initial-position' : '')
                            }
                            onDragOver={handleDragOver}
                            onDragEnter={(e) => handleDragEnter(e, gridId)}
                            onDragLeave={(e) => handleDragLeave(e)}
                            onDrop={(e) => handleDrop(e, x, y)}
                            style={{
                              outline: isDraggedOver ? ('solid 2px ' + tenantColor) : undefined,
                              width: gridWidth,
                              height: gridHeight,
                            }}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}

            <div className='formations-view-team-grid-points-container'>
              {Object.entries(selectedFormationConfig.currentConfig).map(([position, coordinates]) => {

                const gridPosition = getGridPosition(coordinates.x, coordinates.y);

                const isViewMode = selectedFormationConfig.id !== undefined && !selectedFormationConfig.isEditMode;
                const hasAdjacentPositionInXDirection = Object.values(selectedFormationConfig.currentConfig).some(
                  (otherCoordinates) =>
                    coordinates.y === otherCoordinates.y && (coordinates.x === otherCoordinates.x + 1 || coordinates.x === otherCoordinates.x - 1)
                );

                const initialCoordinates = selectedFormationConfig.initialConfig[position as PlatformPosition];
                const positionIsEdited = !initialCoordinates || initialCoordinates.x !== coordinates.x || initialCoordinates.y !== coordinates.y;

                const height = isViewMode ? 25 : gridHeight;
                const width = isViewMode ? (hasAdjacentPositionInXDirection ? 50 : 100) : getGridWidth(getRowWidthPercentage(coordinates.y));

                const left = 'calc(' + (gridPosition.left) + '% - ' + width / 2 + 'px)';
                const top = 'calc(' + (gridPosition.top) + '% - ' + height / 2 + 'px)';

                return (
                  <div
                    key={position + selectedFormationConfig.id}
                    className={
                      'formations-view-position-option fade-in'
                      + (!isViewMode ? ' formations-view-position-option-draggable' : '')
                      + (positionIsEdited ? ' formations-view-position-option-edited' : '')
                    }
                    style={{
                      position: 'absolute',
                      height,
                      width,
                      left,
                      top,
                      zIndex: 2,
                      fontSize: isViewMode ? 14 : undefined,
                    }}
                    draggable={!isViewMode}
                    onDragStart={(e) => {
                      if (!handleDragStart(position as PlatformPosition)) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    onDragEnd={handleDragEnd}
                  >
                    {translate(position, userConfig?.language)}
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      )}

      {!selectedFormationConfig && (
        <div className='formations-view-right-container'>
          <div className='formations-view-right-container-title fade-in'>
            {translate('clubFormation', userConfig?.language)}

            <IconButton
              onClick={() => openTextModal(
                translate('clubFormation', userConfig?.language),
                translate('clubFormationInfo', userConfig?.language),
                { infoType: 'infoIcon', titleKey: 'clubFormationInfo' }
              )}
              icon={<InfoOutlinedIcon style={{ fontSize: 14 }} />}
              size={20}
              style={{ marginTop: 1, marginLeft: 6 }}
            />
          </div>

          <div className='formations-view-right-container-drop-down'>
            <DropDown
              id={'formations-view-formation-drop-down'}
              dropDownOptions={formationOptions}
              selectedOptions={selectedTenantFormation ?? []}
              setSelectedOptions={(options) => setSelectedTenantFormation(options)}
              isDropDownExpanded={isFormationDropDownExpanded}
              setIsDropDownExpanded={setIsFormationDropDownExpanded}
              defaultDropDownText={'selectFormation'}
              defaultDropDownTextColor={'#000000'}
              marginBetweenOptions={3}
              dropDownDisabled={!userConfig?.accessConfig.hasAccessToModifyOwnTeam}
              forceSelection={true}
              maxHeight={'65vh'}
            />
          </div>

          <TextButton
            onClick={() => openConfirmModal(handleUpdateTenantFormation, 'editClubFormation', 'editClubFormationInfo')}
            text={translate('editClubFormation', userConfig?.language)}
            icon={<UpdateIcon style={{ fontSize: 22 }} />}
            style={{ position: 'absolute', top: '29vh', width: 260 }}
            disabled={
              !userConfig?.accessConfig.hasAccessToModifyOwnTeam
              || !selectedTenantFormation
              || selectedTenantFormation.length === 0
              || selectedTenantFormation[0] === tenantSettings?.formation
              || selectedTenantFormation[0] === tenantSettings?.formationConfigs?.[selectedTenantFormation[0]]
            }
          />
        </div>
      )}

    </div>
  );
};
